import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Trash2, Edit, Save, X, Percent, Users, Layers, ChevronDown } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { CommissionStructure, CommissionTier, UserCommissionAssignment, UserProfile, Region, ClientTypeRecord } from "@shared/schema";

// ── Multi-select client type picker ────────────────────────────────────────
function ClientTypeMultiPick({
  selected,
  onChange,
  clientTypes,
}: {
  selected: string[];
  onChange: (ids: string[]) => void;
  clientTypes: ClientTypeRecord[] | undefined;
}) {
  const [open, setOpen] = useState(false);
  const active = clientTypes?.filter(ct => ct.isActive) ?? [];

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id]);
  };

  const label =
    selected.length === 0
      ? "All Client Types"
      : selected.length === 1
      ? active.find(ct => ct.id === selected[0])?.name ?? selected[0]
      : `${selected.length} types selected`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          className="w-full justify-between font-normal"
          data-testid="select-structure-client-types"
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="w-4 h-4 ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2" align="start">
        <button
          type="button"
          className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-muted/60 text-muted-foreground"
          onClick={() => onChange([])}
        >
          <Checkbox checked={selected.length === 0} readOnly className="pointer-events-none" />
          All Client Types
        </button>
        <div className="my-1 border-t" />
        {active.map(ct => (
          <button
            key={ct.id}
            type="button"
            className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-muted/60"
            onClick={() => toggle(ct.id)}
          >
            <Checkbox checked={selected.includes(ct.id)} readOnly className="pointer-events-none" />
            {ct.name}
          </button>
        ))}
        {active.length === 0 && (
          <p className="px-2 py-1.5 text-xs text-muted-foreground">No client types configured.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ── Tier form (for tiered / per_run structures) ────────────────────────────
interface TierFormProps {
  structureId: string;
  onAddTier: (data: { commissionStructureId: string; minThreshold: string; maxThreshold: string | null; percentage: string }) => void;
  isAdding: boolean;
}

function TierForm({ structureId, onAddTier, isAdding }: TierFormProps) {
  const { toast } = useToast();
  const [tier, setTier] = useState({ minThreshold: "", maxThreshold: "", percentage: "" });

  const handleAdd = () => {
    if (!tier.minThreshold || !tier.percentage) {
      toast({ title: "Error", description: "Min threshold and percentage are required", variant: "destructive" });
      return;
    }
    onAddTier({
      commissionStructureId: structureId,
      minThreshold: tier.minThreshold,
      maxThreshold: tier.maxThreshold || null,
      percentage: tier.percentage,
    });
    setTier({ minThreshold: "", maxThreshold: "", percentage: "" });
  };

  return (
    <div className="flex items-end gap-3 flex-wrap">
      <div className="space-y-2">
        <Label>Min Avg Daily ($)</Label>
        <Input type="number" min="0" value={tier.minThreshold} onChange={(e) => setTier({ ...tier, minThreshold: e.target.value })} placeholder="0" className="w-32" data-testid={`input-tier-min-${structureId}`} />
      </div>
      <div className="space-y-2">
        <Label>Max Avg Daily ($)</Label>
        <Input type="number" min="0" value={tier.maxThreshold} onChange={(e) => setTier({ ...tier, maxThreshold: e.target.value })} placeholder="No limit" className="w-32" data-testid={`input-tier-max-${structureId}`} />
      </div>
      <div className="space-y-2">
        <Label>Commission %</Label>
        <Input type="number" min="0" max="100" step="0.1" value={tier.percentage} onChange={(e) => setTier({ ...tier, percentage: e.target.value })} placeholder="20" className="w-24" data-testid={`input-tier-percentage-${structureId}`} />
      </div>
      <Button onClick={handleAdd} disabled={isAdding} data-testid={`button-add-tier-${structureId}`}>
        {isAdding && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
        <Plus className="w-4 h-4 mr-2" />
        Add Tier
      </Button>
    </div>
  );
}

// ── Main dialog ────────────────────────────────────────────────────────────
interface ManageCommissionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inPanel?: boolean;
}

type CommissionType = "flat" | "tiered" | "per_run";

const TRIGGER_OPTIONS = [
  { value: "", label: "Use workspace default" },
  { value: "on_balance", label: "Balance is paid" },
  { value: "on_deposit", label: "Deposit is received" },
  { value: "split_50_50", label: "Split 50/50 (half on deposit, half on balance)" },
  { value: "on_run_complete", label: "Run is completed (per-run only)" },
];

const TRIGGER_LABELS: Record<string, string> = {
  on_balance: "Balance paid",
  on_deposit: "Deposit paid",
  split_50_50: "50/50 split",
  on_run_complete: "Run complete",
};

function triggerLabel(mode: string | null | undefined, workspaceDefault: string): string {
  if (!mode) return `${TRIGGER_LABELS[workspaceDefault] ?? workspaceDefault} (workspace default)`;
  return TRIGGER_LABELS[mode] ?? mode;
}

export function ManageCommissionsDialog({ open, onOpenChange, inPanel = false }: ManageCommissionsDialogProps) {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("structures");
  const [editingStructure, setEditingStructure] = useState<CommissionStructure | null>(null);
  const [isCreatingStructure, setIsCreatingStructure] = useState(false);
  const [structureForm, setStructureForm] = useState({
    name: "",
    type: "flat" as CommissionType,
    flatPercentage: "10",
    isActive: true,
    clientTypeIds: [] as string[],
    triggerMode: "" as string,
  });

  const { data: structures, isLoading: loadingStructures } = useQuery<CommissionStructure[]>({
    queryKey: ["/api/billing/commission-structures"],
    enabled: open,
  });

  const { data: allTiers } = useQuery<CommissionTier[]>({
    queryKey: ["/api/billing/commission-tiers"],
    enabled: open,
  });

  const { data: assignments, isLoading: loadingAssignments } = useQuery<UserCommissionAssignment[]>({
    queryKey: ["/api/billing/user-commission-assignments"],
    enabled: open,
  });

  const { data: users } = useQuery<UserProfile[]>({
    queryKey: ["/api/users"],
    enabled: open,
  });

  const { data: regions } = useQuery<Region[]>({
    queryKey: ["/api/regions"],
    enabled: open,
  });

  const { data: clientTypes } = useQuery<ClientTypeRecord[]>({
    queryKey: ["/api/client-types"],
    enabled: open,
  });

  const { data: commissionSettings } = useQuery<{ commissionTriggerMode: string }>({
    queryKey: ["/api/admin/commission-settings"],
    enabled: open,
  });

  const workspaceDefault = commissionSettings?.commissionTriggerMode ?? "on_balance";

  const createStructureMutation = useMutation({
    mutationFn: async (data: typeof structureForm) => {
      const response = await apiRequest("POST", "/api/billing/commission-structures", {
        name: data.name,
        structureType: data.type,
        flatPercentage: data.type === "flat" ? data.flatPercentage : null,
        isActive: data.isActive,
        clientTypeIds: data.clientTypeIds,
        triggerMode: data.type === "per_run" ? "on_run_complete" : (data.triggerMode || null),
        createdBy: "system",
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/commission-structures"] });
      toast({ title: "Success", description: "Commission structure created" });
      resetStructureForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create structure", variant: "destructive" });
    },
  });

  const updateStructureMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: typeof structureForm }) => {
      const response = await apiRequest("PATCH", `/api/billing/commission-structures/${id}`, {
        name: data.name,
        structureType: data.type,
        flatPercentage: data.type === "flat" ? data.flatPercentage : null,
        isActive: data.isActive,
        clientTypeIds: data.clientTypeIds,
        triggerMode: data.type === "per_run" ? "on_run_complete" : (data.triggerMode || null),
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/commission-structures"] });
      toast({ title: "Success", description: "Commission structure updated" });
      setEditingStructure(null);
      resetStructureForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update structure", variant: "destructive" });
    },
  });

  const deleteStructureMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/billing/commission-structures/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/commission-structures"] });
      toast({ title: "Success", description: "Commission structure deleted" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to delete structure", variant: "destructive" });
    },
  });

  const createTierMutation = useMutation({
    mutationFn: async (data: { commissionStructureId: string; minThreshold: string; maxThreshold: string | null; percentage: string }) => {
      const response = await apiRequest("POST", "/api/billing/commission-tiers", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/commission-tiers"] });
      toast({ title: "Success", description: "Tier added" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to add tier", variant: "destructive" });
    },
  });

  const deleteTierMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/billing/commission-tiers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/commission-tiers"] });
      toast({ title: "Success", description: "Tier deleted" });
    },
  });

  const assignCommissionMutation = useMutation({
    mutationFn: async (data: { userId: string; commissionStructureId: string }) => {
      const response = await apiRequest("POST", "/api/billing/user-commission-assignments", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/user-commission-assignments"] });
      toast({ title: "Success", description: "Commission assigned" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to assign commission", variant: "destructive" });
    },
  });

  const resetStructureForm = () => {
    setStructureForm({ name: "", type: "flat", flatPercentage: "10", isActive: true, clientTypeIds: [], triggerMode: "" });
    setIsCreatingStructure(false);
    setEditingStructure(null);
  };

  const handleCreateStructure = () => {
    if (!structureForm.name.trim()) {
      toast({ title: "Error", description: "Name is required", variant: "destructive" });
      return;
    }
    createStructureMutation.mutate(structureForm);
  };

  const handleUpdateStructure = () => {
    if (!editingStructure) return;
    updateStructureMutation.mutate({ id: editingStructure.id, data: structureForm });
  };

  const startEditingStructure = (structure: CommissionStructure) => {
    setEditingStructure(structure);
    // clientTypeIds: prefer the new array field; fall back to legacy single
    const ids: string[] =
      Array.isArray((structure as any).clientTypeIds) && (structure as any).clientTypeIds.length > 0
        ? (structure as any).clientTypeIds
        : structure.clientTypeId
        ? [structure.clientTypeId]
        : [];
    setStructureForm({
      name: structure.name,
      type: (structure.structureType || "flat") as CommissionType,
      flatPercentage: structure.flatPercentage || "10",
      isActive: structure.isActive ?? true,
      clientTypeIds: ids,
      triggerMode: (structure as any).triggerMode ?? "",
    });
    setIsCreatingStructure(false);
  };

  const getStructureTiers = (structureId: string) =>
    allTiers?.filter(t => t.commissionStructureId === structureId) || [];

  const getUserAssignment = (userId: string) =>
    assignments?.find(a => a.userId === userId);

  // Resolve client type names from IDs
  const resolveCtNames = (ids: string[] | undefined | null, singleId?: string | null): string => {
    const arr = Array.isArray(ids) && ids.length > 0 ? ids : singleId ? [singleId] : [];
    if (arr.length === 0) return "All Types";
    return arr.map(id => clientTypes?.find(ct => ct.id === id)?.name ?? id).join(", ");
  };

  if (!open && !inPanel) return null;

  const innerContent = (<>
    {!inPanel && (
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <Percent className="w-5 h-5" />
          Manage Commissions
        </DialogTitle>
        <DialogDescription>
          Configure commission structures, tiers, and user assignments. Calculated results appear automatically on the Billing → Commissions page.
        </DialogDescription>
      </DialogHeader>
    )}

    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
      <TabsList className="grid w-full grid-cols-3">
        <TabsTrigger value="structures" className="flex items-center gap-1.5 text-xs sm:text-sm" data-testid="tab-structures">
          <Layers className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Structures</span>
        </TabsTrigger>
        <TabsTrigger value="tiers" className="flex items-center gap-1.5 text-xs sm:text-sm" data-testid="tab-tiers">
          <Percent className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Tiers</span>
        </TabsTrigger>
        <TabsTrigger value="assignments" className="flex items-center gap-1.5 text-xs sm:text-sm" data-testid="tab-assignments">
          <Users className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">Assignments</span>
        </TabsTrigger>
      </TabsList>

      <div className="flex-1 overflow-auto mt-4">

        {/* ── STRUCTURES ── */}
        <TabsContent value="structures" className="m-0">
          <div className="space-y-4">
            {!isCreatingStructure && !editingStructure && (
              <Button onClick={() => setIsCreatingStructure(true)} data-testid="button-add-structure">
                <Plus className="w-4 h-4 mr-2" />
                Add Commission Structure
              </Button>
            )}

            {(isCreatingStructure || editingStructure) && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">
                    {editingStructure ? "Edit Structure" : "New Commission Structure"}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">

                  {/* Name + Type */}
                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="structure-name">Name</Label>
                      <Input
                        id="structure-name"
                        value={structureForm.name}
                        onChange={(e) => setStructureForm({ ...structureForm, name: e.target.value })}
                        placeholder="e.g., Senior Living"
                        data-testid="input-structure-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="structure-type">Type</Label>
                      <Select
                        value={structureForm.type}
                        onValueChange={(value: CommissionType) =>
                          setStructureForm({
                            ...structureForm,
                            type: value,
                            // lock trigger for per_run
                            triggerMode: value === "per_run" ? "on_run_complete" : structureForm.triggerMode,
                          })
                        }
                      >
                        <SelectTrigger id="structure-type" data-testid="select-structure-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="flat">Flat Percentage</SelectItem>
                          <SelectItem value="tiered">Tiered (Avg Daily Revenue)</SelectItem>
                          <SelectItem value="per_run">Per Run (Avg Daily Revenue × Run)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Client Type multi-pick */}
                  <div className="space-y-2">
                    <Label>Client Type</Label>
                    <ClientTypeMultiPick
                      selected={structureForm.clientTypeIds}
                      onChange={(ids) => setStructureForm({ ...structureForm, clientTypeIds: ids })}
                      clientTypes={clientTypes}
                    />
                    <p className="text-xs text-muted-foreground">
                      Restrict to specific client types, or leave as "All Client Types".
                    </p>
                  </div>

                  {/* Pay commission when... (per structure) */}
                  <div className="space-y-2">
                    <Label htmlFor="structure-trigger">Pay commission when…</Label>
                    {structureForm.type === "per_run" ? (
                      <div className="px-3 py-2 rounded-md border bg-muted/40 text-sm text-muted-foreground">
                        Run is completed — locked for Per Run structures
                      </div>
                    ) : (
                      <Select
                        value={structureForm.triggerMode || ""}
                        onValueChange={(v) => setStructureForm({ ...structureForm, triggerMode: v })}
                      >
                        <SelectTrigger id="structure-trigger" data-testid="select-structure-trigger">
                          <SelectValue placeholder={`Workspace default (${TRIGGER_LABELS[workspaceDefault] ?? workspaceDefault})`} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">Workspace default ({TRIGGER_LABELS[workspaceDefault] ?? workspaceDefault})</SelectItem>
                          <SelectItem value="on_balance">Balance is paid</SelectItem>
                          <SelectItem value="on_deposit">Deposit is received</SelectItem>
                          <SelectItem value="split_50_50">Split 50/50 (half on deposit, half on balance)</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Applies on the next commission recalculation. Already-paid invoices keep their existing accruals.
                    </p>
                  </div>

                  {/* Flat % */}
                  {structureForm.type === "flat" && (
                    <div className="space-y-2">
                      <Label htmlFor="flat-percentage">Commission Percentage (%)</Label>
                      <Input
                        id="flat-percentage"
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        value={structureForm.flatPercentage}
                        onChange={(e) => setStructureForm({ ...structureForm, flatPercentage: e.target.value })}
                        placeholder="10"
                        data-testid="input-flat-percentage"
                      />
                    </div>
                  )}

                  {/* Tiered hint */}
                  {structureForm.type === "tiered" && (
                    <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                      After creating, go to the <strong>Tiers</strong> tab to define avg-daily-revenue thresholds. The Billing page will automatically group events by region, compute <em>total revenue ÷ event days</em>, and apply the matching tier percentage.
                    </p>
                  )}

                  {/* Per-run hint */}
                  {structureForm.type === "per_run" && (
                    <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md">
                      After creating, go to the <strong>Tiers</strong> tab to define avg-daily-revenue thresholds. Commission is calculated as <em>avg booking dollars/day in the run × matching tier %</em> and is paid when the run is completed (balance marked paid).
                    </p>
                  )}

                  {/* Active toggle */}
                  <div className="flex items-center gap-3">
                    <Switch
                      id="structure-active"
                      checked={structureForm.isActive}
                      onCheckedChange={(checked) => setStructureForm({ ...structureForm, isActive: checked })}
                      data-testid="switch-structure-active"
                    />
                    <Label htmlFor="structure-active">Active</Label>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={editingStructure ? handleUpdateStructure : handleCreateStructure}
                      disabled={createStructureMutation.isPending || updateStructureMutation.isPending}
                      data-testid="button-save-structure"
                    >
                      {(createStructureMutation.isPending || updateStructureMutation.isPending) && (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      )}
                      <Save className="w-4 h-4 mr-2" />
                      {editingStructure ? "Update" : "Create"}
                    </Button>
                    <Button variant="outline" onClick={resetStructureForm} data-testid="button-cancel-structure">
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {loadingStructures ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : structures && structures.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Client Type</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead>Pay When</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {structures.map((structure) => {
                    const ctNames = resolveCtNames((structure as any).clientTypeIds, structure.clientTypeId);
                    const tMode = (structure as any).triggerMode;
                    return (
                      <TableRow key={structure.id}>
                        <TableCell className="font-medium">{structure.name}</TableCell>
                        <TableCell>
                          <Badge variant={structure.structureType === "flat" ? "secondary" : structure.structureType === "per_run" ? "default" : "outline"}>
                            {structure.structureType === "flat" ? "Flat" : structure.structureType === "per_run" ? "Per Run" : "Tiered"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground max-w-[160px] truncate" title={ctNames}>{ctNames}</TableCell>
                        <TableCell>
                          {structure.structureType === "flat"
                            ? `${structure.flatPercentage || 0}%`
                            : <span className="text-muted-foreground">See tiers</span>}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {triggerLabel(tMode, workspaceDefault)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={structure.isActive ? "default" : "secondary"}>
                            {structure.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => startEditingStructure(structure)} data-testid={`button-edit-structure-${structure.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => deleteStructureMutation.mutate(structure.id)} disabled={deleteStructureMutation.isPending} data-testid={`button-delete-structure-${structure.id}`}>
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                No commission structures defined. Click "Add Commission Structure" to create one.
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── TIERS ── */}
        <TabsContent value="tiers" className="m-0">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Define avg daily revenue thresholds for tiered and per-run structures. The Billing Commissions page groups events by region, computes <strong>total revenue ÷ event days</strong>, then applies the matching tier percentage.
            </p>

            {structures?.filter(s => s.structureType === "tiered" || s.structureType === "per_run").map((structure) => (
              <Card key={structure.id}>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    {structure.name}
                    <Badge variant="outline">{structure.structureType === "per_run" ? "Per Run" : "Tiered"}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {getStructureTiers(structure.id).length > 0 ? (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Min Avg Daily ($)</TableHead>
                          <TableHead>Max Avg Daily ($)</TableHead>
                          <TableHead>Commission %</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getStructureTiers(structure.id)
                          .sort((a, b) => parseFloat(a.minThreshold) - parseFloat(b.minThreshold))
                          .map((tier) => (
                            <TableRow key={tier.id}>
                              <TableCell>${parseFloat(tier.minThreshold).toLocaleString()}</TableCell>
                              <TableCell>{tier.maxThreshold ? `$${parseFloat(tier.maxThreshold).toLocaleString()}` : "No limit"}</TableCell>
                              <TableCell>{tier.percentage}%</TableCell>
                              <TableCell className="text-right">
                                <Button variant="ghost" size="icon" onClick={() => deleteTierMutation.mutate(tier.id)} disabled={deleteTierMutation.isPending} data-testid={`button-delete-tier-${tier.id}`}>
                                  <Trash2 className="w-4 h-4 text-destructive" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">No tiers defined for this structure.</p>
                  )}

                  <TierForm
                    structureId={structure.id}
                    onAddTier={(data) => createTierMutation.mutate(data)}
                    isAdding={createTierMutation.isPending}
                  />
                </CardContent>
              </Card>
            ))}

            {(!structures || structures.filter(s => s.structureType === "tiered" || s.structureType === "per_run").length === 0) && (
              <div className="text-center py-8 text-muted-foreground">
                No tiered or per-run structures yet. Create one in the Structures tab first.
              </div>
            )}
          </div>
        </TabsContent>

        {/* ── ASSIGNMENTS ── */}
        <TabsContent value="assignments" className="m-0">
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Assign commission structures to sales representatives. The Billing Commissions page will automatically calculate their earnings by trip.
            </p>

            {loadingAssignments ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Commission Structure</TableHead>
                    <TableHead>Override %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users?.filter(u => u.role === "sales_rep" || u.role === "admin").map((user) => {
                    const assignment = getUserAssignment(user.id);
                    return (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.firstName} {user.lastName}</TableCell>
                        <TableCell><Badge variant="outline">{user.role}</Badge></TableCell>
                        <TableCell>
                          <Select
                            value={assignment?.commissionStructureId || ""}
                            onValueChange={(value) => {
                              if (value) assignCommissionMutation.mutate({ userId: user.id, commissionStructureId: value });
                            }}
                          >
                            <SelectTrigger className="w-48" data-testid={`select-assignment-${user.id}`}>
                              <SelectValue placeholder="Select structure" />
                            </SelectTrigger>
                            <SelectContent>
                              {structures?.filter(s => s.isActive).map((structure) => (
                                <SelectItem key={structure.id} value={structure.id}>
                                  {structure.name} ({structure.structureType === "flat" ? `${structure.flatPercentage}%` : structure.structureType === "per_run" ? "Per Run" : "Tiered"})
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {assignment?.customFlatOverride
                            ? <Badge>{assignment.customFlatOverride}%</Badge>
                            : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {(!users || users.filter(u => u.role === "sales_rep" || u.role === "admin").length === 0) && (
              <div className="text-center py-8 text-muted-foreground">No sales representatives or admins found.</div>
            )}
          </div>
        </TabsContent>

      </div>
    </Tabs>

    <DialogFooter>
      <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="button-close-commissions-dialog">
        Close
      </Button>
    </DialogFooter>
  </>);

  if (inPanel) {
    return (
      <div className="w-full h-full flex flex-col overflow-auto p-4 sm:p-6">
        {innerContent}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-full sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {innerContent}
      </DialogContent>
    </Dialog>
  );
}
